import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import "../../Styles/WHMIS.css";
import WHMISImage from "../../assets/WHMIS.png";
import GHSImage from "../../assets/GHS.png";

// ---------------- MODULES LIST ----------------
const modules = [
  { id: "00", title: "Welcome and Introduction" },
  { id: "01", title: "WHMIS Program Page 01" },
  { id: "02", title: "WHMIS Program Page 02" },
  { id: "03", title: "WHMIS Program Page 03" },
  { id: "04", title: "WHMIS Program Page 04" },
  ...Array.from({ length: 63 }, (_, i) => {
    const id = (i + 5).toString().padStart(2, "0");
    return { id, title: `WHMIS Program Page ${id}` };
  }),
  { id: "68", title: "WHMIS Quiz" },
];

// ---------------- HELPERS ----------------
const getWHMISImage = (id) => {
  try {
    return require(`../../assets/WHMIS${id}.png`);
  } catch (err) {
    console.warn(`Image WHMIS${id}.png not found`);
    return null;
  }
};

const baseContent = {
  "00": (
    <div>
      <p>Hi!</p>
      <p>Welcome to our WHMIS Reading and Quiz section.</p>
      <p>
        You will be presented with reading slides. Please familiarize yourself
        with the content. At the end, there will be a quiz you must pass.
      </p>
      <p>There is no time limit or number of tries.</p>
      <p>Click Next to get started!</p>
    </div>
  ),
  "01": (
    <div>
      <h1>
        <strong>W</strong>orkplace <strong>H</strong>azardous <strong>M</strong>
        aterial <strong>I</strong>nformation <strong>S</strong>ystem
      </h1>
    </div>
  ),
  "02": (
    <div className="whmis-objectives">
      <p className="objective-title">Workshop Objectives</p>
      <p className="objective-subtitle">When you complete this training,</p>
      <p className="objective-subtitle">you should be able to:</p>
      <ul className="objective-list">
        <li>Apply knowledge of WHMIS in your workplace.</li>
        <li>Describe requirements for supplier and workplace labels.</li>
        <li>
          Find information on the Safety Data Sheet (SDS) to use the product
          safely.
        </li>
        <li>Explain health and safety hazards you may encounter.</li>
        <li>
          Identify WHMIS pictograms and precautions when handling materials.
        </li>
        <li>Describe your rights and responsibilities related to WHMIS.</li>
      </ul>
    </div>
  ),
  "03": (
    <div className="sds-content">
      <img
        src={WHMISImage}
        alt="Safety Data Sheet Example"
        className="sds-image"
      />
    </div>
  ),
  "04": (
    <div className="sds-content">
      <img
        src={GHSImage}
        alt="Safety Data Sheet Example"
        className="sds-image"
      />
    </div>
  ),
};

// For 05–67, generate dynamically
for (let i = 5; i <= 67; i++) {
  const id = i.toString().padStart(2, "0");
  baseContent[id] = (
    <div className="sds-content">
      <img
        src={getWHMISImage(id)}
        alt={`WHMIS Example ${id}`}
        className="sds-image"
      />
    </div>
  );
}

// Quiz questions for module 68
const quizQuestions = [
  {
    question:
      "1. True or False? Under WHMIS regulations there are no specific requirements for supplier or workplace labels.",
    options: ["Yes", "No"],
    answer: 1,
  },
  {
    question:
      "2. The two most important physical parts of WHMIS are ______________ and ___________________.",
    options: [
      "First-aid procedures and safety data sheets (SDSs)",
      "Labels and safety data sheets (SDSs)",
      "Danger ratings and content/ingreduients",
      "Suplier information and labels",
    ],
    answer: 1,
  },
  {
    question:
      "3. True or False? SDSs and supplier labels will show a signal word (Danger or Warning) that indicates the severity of the hazard, if assigned.",
    options: ["Yes", "No"],
    answer: 0,
  },
  {
    question:
      "4. True or False? If you notice that the label is missing, damaged or no longer readable from a product used in the workplace you are required to inform your supervisor.",
    options: ["Yes", "No"],
    answer: 0,
  },
  {
    question:
      "5. What does the hazard pictogram that appears on the supplier label tell you?",
    options: [
      "Primary hazard(s) posed by a product",
      "Exposure conditions",
      "Supplier information",
      "Emergency contact information",
    ],
    answer: 0,
  },
  {
    question:
      "6. Which of the following must be included on a workplace label?",
    options: [
      "Combustibility",
      "Primary hazard posed by a product",
      "Supplier's name",
      'The statement "Refer to SDS"',
    ],
    answer: 3,
  },
  {
    question:
      "7. A ________ hazard is a sudden reaction such as a fire, explosion or corrosion",
    options: ["Biological", "Chronic", "Health", "Physical"],
    answer: 3,
  },
  {
    question:
      "8. The hazard class and category are a guide to the ________________, _____________, and ____________________.",
    options: [
      "Subcategory; type of hazard; ignition sources",
      "Type of hazard; degree of hazard; precautions to follow.",
      "Hazard product; manufacturer's name;exposure limits.",
      "Type of hazard; barriers to use; supplier's address.",
    ],
    answer: 1,
  },
  {
    question: (
      <>
        9. Please match the hazard description with the right pictogram. <br />
        <img
          src={require("../../assets/pictogram1WHMIS.png")}
          alt="Hazard Pictograms"
          style={{ width: "500px", marginTop: "10px" }}
        />
        <img
          src={require("../../assets/pictogram2WHMIS.png")}
          alt="Hazard Pictograms"
          style={{ width: "500px", marginTop: "10px" }}
        />
      </>
    ),
    type: "matching",
    pairs: [
      { id: 1, text: "Short exposure can cause death" },
      { id: 2, text: "Fire hazard" },
      { id: 3, text: "Health hazard" },
      { id: 4, text: "Gas under pressure" },
      { id: 5, text: "Skin or eye irritation" },
      { id: 6, text: "Highly self-reactive" },
      { id: 7, text: "Corrosive" },
      { id: 8, text: "Biohazardous infectious materials" },
    ],
    options: ["a", "b", "c", "d", "e", "f", "g", "h"],
    // Default (unshuffled) answer — derived from Q9_BASE_DATA order:
    // WHMISIMG1=a(Health hazard/3), WHMISIMG2=b(Skin or eye/5),
    // WHIMSIMG3=c(Short exposure/1), WHIMSIMG4=d(Fire hazard/2),
    // WHIMSIMG5=e(Gas under pressure/4), WHIMSIMG6=f(Highly self-reactive/6),
    // WHIMSIMG7=g(Corrosive/7), WHIMSIMG8=h(Biohazardous/8)
    answer: { 1: "c", 2: "d", 3: "a", 4: "e", 5: "b", 6: "f", 7: "g", 8: "h" },
  },
  {
    question: (
      <>
        10. Please match the Label Elements with its purpose. <br />
        <p style={{ marginTop: "2rem" }}>
          a. Describes the nature of the hazard
        </p>
        <p>b. Name that matches the SDS</p>
        <p>c. Alerts to the severity of the hazard</p>
        <p>d. Describes the measures to prevent or minimize the effects</p>
        <p style={{ marginBottom: "2rem" }}>
          e. Name, address, and telephone number of supplier
        </p>
      </>
    ),
    type: "matching",
    pairs: [
      { id: 1, text: "Signal Word" },
      { id: 2, text: "Hazard Statement" },
      { id: 3, text: "Precautionary Statement" },
      { id: 4, text: "Product Identity" },
      { id: 5, text: "Supplier Identification" },
    ],
    options: ["a", "b", "c", "d", "e"],
    answer: { 1: "a", 2: "d", 3: "e", 4: "c", 5: "b" },
  },
];

// ---------------- Q9 PICTOGRAM DATA ----------------
// Maps each individual image file to its correct hazard description and pair ID.
// pairId matches the id values in quizQuestions[8].pairs.
const WHMIS_PICTOGRAM_SRCS = {
  WHMISIMG1: require("../../assets/WHMISIMG1.png"),
  WHMISIMG2: require("../../assets/WHMISIMG2.png"),
  WHIMSIMG3: require("../../assets/WHIMSIMG3.png"),
  WHIMSIMG4: require("../../assets/WHIMSIMG4.png"),
  WHIMSIMG5: require("../../assets/WHIMSIMG5.png"),
  WHIMSIMG6: require("../../assets/WHIMSIMG6.png"),
  WHIMSIMG7: require("../../assets/WHIMSIMG7.png"),
  WHIMSIMG8: require("../../assets/WHIMSIMG8.png"),
};

const Q9_BASE_DATA = [
  { imgId: "WHMISIMG1", pairId: 3, hazard: "Highly self-reactive" },
  { imgId: "WHMISIMG2", pairId: 5, hazard: "Corrosive" },
  { imgId: "WHIMSIMG3", pairId: 1, hazard: "Health hazard" },
  { imgId: "WHIMSIMG4", pairId: 2, hazard: "Skin or eye irritation" },
  { imgId: "WHIMSIMG5", pairId: 4, hazard: "Short exposure can cause death" },
  { imgId: "WHIMSIMG6", pairId: 6, hazard: "Fire hazard" },
  { imgId: "WHIMSIMG7", pairId: 7, hazard: "Gas under pressure" },
  { imgId: "WHIMSIMG8", pairId: 8, hazard: "Biohazardous infectious materials" },
];

const Q9_LETTERS = ["a", "b", "c", "d", "e", "f", "g", "h"];

/** Fisher-Yates shuffle (in-place clone) */
function fisherYates(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Returns a session-stable shuffle for Q9.
 * - Images (columns) are shuffled → letters a–h reassigned per position
 * - Pairs (description rows) are also shuffled
 * - Answer map is recomputed from the image shuffle so it stays correct
 */
function getSessionShuffledQ9() {
  const SESSION_KEY = "whmis_q9_order_v2"; // bumped to invalidate stale caches
  const saved = sessionStorage.getItem(SESSION_KEY);

  if (saved) {
    try {
      const { imgOrder, pairOrder } = JSON.parse(saved);
      if (
        Array.isArray(imgOrder) && imgOrder.length === 8 &&
        Array.isArray(pairOrder) && pairOrder.length === 8
      ) {
        const shuffledImages = imgOrder.map((id) => Q9_BASE_DATA.find((d) => d.imgId === id));
        const shuffledPairs  = pairOrder.map((pid) => ({ id: pid, text: Q9_BASE_DATA.find((d) => d.pairId === pid).hazard }));
        const answer = {};
        shuffledImages.forEach((img, i) => { answer[img.pairId] = Q9_LETTERS[i]; });
        return { shuffledImages, shuffledPairs, answer };
      }
    } catch (_) { /* fall through */ }
  }

  const shuffledImages = fisherYates(Q9_BASE_DATA);
  const shuffledPairs  = fisherYates(Q9_BASE_DATA.map((d) => ({ id: d.pairId, text: d.hazard })));
  const answer = {};
  shuffledImages.forEach((img, i) => { answer[img.pairId] = Q9_LETTERS[i]; });

  sessionStorage.setItem(SESSION_KEY, JSON.stringify({
    imgOrder:  shuffledImages.map((d) => d.imgId),
    pairOrder: shuffledPairs.map((p) => p.id),
  }));

  return { shuffledImages, shuffledPairs, answer };
}

// ---------------- Q10 DATA & SHUFFLE ----------------
// Each entry links a description text to the pair (term) it correctly answers.
// pairId matches the id values in quizQuestions[9].pairs.
// Correct mappings verified against hints:
//   Signal Word (1)            → c. Alerts to the severity of the hazard
//   Hazard Statement (2)       → a. Describes the nature of the hazard
//   Precautionary Statement (3)→ d. Describes the measures to prevent or minimize the effects
//   Product Identity (4)       → b. Name that matches the SDS
//   Supplier Identification (5)→ e. Name, address, and telephone number of supplier
const Q10_BASE_DATA = [
  { descId: "a", pairId: 2, description: "Describes the nature of the hazard" },
  { descId: "b", pairId: 4, description: "Name that matches the SDS" },
  { descId: "c", pairId: 1, description: "Alerts to the severity of the hazard" },
  { descId: "d", pairId: 3, description: "Describes the measures to prevent or minimize the effects" },
  { descId: "e", pairId: 5, description: "Name, address, and telephone number of supplier" },
];

const Q10_LETTERS = ["a", "b", "c", "d", "e"];

/**
 * Returns a session-stable shuffle for Q10.
 * - Descriptions (columns shown above the rows) are shuffled → letters a–e reassigned
 * - Pairs (term rows) are also shuffled
 * - Answer map is recomputed so grading stays correct regardless of shuffle
 */
function getSessionShuffledQ10() {
  const SESSION_KEY = "whmis_q10_order_v2";
  const saved = sessionStorage.getItem(SESSION_KEY);

  if (saved) {
    try {
      const { descOrder, pairOrder } = JSON.parse(saved);
      if (
        Array.isArray(descOrder) && descOrder.length === 5 &&
        Array.isArray(pairOrder) && pairOrder.length === 5
      ) {
        const shuffledDescs = descOrder.map((id) => Q10_BASE_DATA.find((d) => d.descId === id));
        const q10Pairs = quizQuestions[9].pairs;
        const shuffledPairs = pairOrder.map((pid) => q10Pairs.find((p) => p.id === pid));
        const answer = {};
        shuffledDescs.forEach((desc, i) => { answer[desc.pairId] = Q10_LETTERS[i]; });
        return { shuffledDescs, shuffledPairs, answer };
      }
    } catch (_) { /* fall through */ }
  }

  const shuffledDescs  = fisherYates(Q10_BASE_DATA);
  const shuffledPairs  = fisherYates(quizQuestions[9].pairs);
  const answer = {};
  shuffledDescs.forEach((desc, i) => { answer[desc.pairId] = Q10_LETTERS[i]; });

  sessionStorage.setItem(SESSION_KEY, JSON.stringify({
    descOrder: shuffledDescs.map((d) => d.descId),
    pairOrder: shuffledPairs.map((p) => p.id),
  }));

  return { shuffledDescs, shuffledPairs, answer };
}

// ---------------- ACTIVE QUIZ (with session shuffles applied) ----------------
const { shuffledImages: q9Images, shuffledPairs: q9Pairs, answer: q9Answer } = getSessionShuffledQ9();
const { shuffledDescs: q10Descs, shuffledPairs: q10Pairs, answer: q10Answer } = getSessionShuffledQ10();

const activeQuizQuestions = quizQuestions.map((q, idx) => {
  if (idx === 8) {
    return {
      ...q,
      question: (
        <>
          <span>9. Please match the hazard description with the right pictogram.</span>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
            marginTop: "16px",
            marginBottom: "16px",
          }}>
            {q9Images.map((img, i) => (
              <div key={img.imgId} style={{ textAlign: "center" }}>
                <img
                  src={WHMIS_PICTOGRAM_SRCS[img.imgId]}
                  alt={img.hazard}
                  style={{ width: "150px", height: "150px", objectFit: "contain", border: "1px solid #eee", borderRadius: "6px" }}
                />
                <div style={{ fontWeight: "bold", marginTop: "8px", fontSize: "15px" }}>{Q9_LETTERS[i]}.</div>
              </div>
            ))}
          </div>
        </>
      ),
      pairs: q9Pairs,
      answer: q9Answer,
    };
  }
  if (idx === 9) {
    return {
      ...q,
      question: (
        <>
          <span>10. Please match the Label Elements with its purpose.</span>
          <div style={{ marginTop: "1.25rem", marginBottom: "1.25rem" }}>
            {q10Descs.map((desc, i) => (
              <p key={desc.descId} style={{ margin: "6px 0", fontSize: "14px", color: "#333" }}>
                <strong>{Q10_LETTERS[i]}.</strong> {desc.description}
              </p>
            ))}
          </div>
        </>
      ),
      pairs: q10Pairs,
      options: Q10_LETTERS,
      answer: q10Answer,
    };
  }
  return q;
});

// ---------------- COMPONENT ----------------
export default function TrainingInterface() {
  const navigate = useNavigate();
  const accountId = localStorage.getItem("accountId");
  const itemId = "whmis";

  // ✅ Check if Health & Safety is completed (prerequisite)
  const [healthSafetyCompleted, setHealthSafetyCompleted] = useState(false);
  const [showPrerequisiteWarning, setShowPrerequisiteWarning] = useState(false);

  useEffect(() => {
    const healthSafetyStatus = localStorage.getItem(`status_${accountId}_health-safety`);
    const isComplete = healthSafetyStatus === "complete";
    setHealthSafetyCompleted(isComplete);
    setShowPrerequisiteWarning(!isComplete);
  }, [accountId]);

  const [unlockedModules, setUnlockedModules] = useState(() => {
    const savedStatus = localStorage.getItem(`status_${accountId}_whmis`);
    const totalModules = modules.length;
    
    const saved = localStorage.getItem("whmis_unlockedModules");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // ✅ If ALL modules are saved but quiz not complete → stale data, start fresh
          if (parsed.length >= totalModules && savedStatus !== "complete") {
            console.log('[WHMIS] Stale complete data detected, resetting progress');
            localStorage.removeItem("whmis_unlockedModules");
            localStorage.removeItem("whmis_selectedAnswers");
            localStorage.removeItem("whmis_currentModule");
            localStorage.removeItem("whmis_quizIndex");
            return ["00"];
          }
          // ✅ Partial progress - restore the modules the user actually viewed
          return Array.from(new Set(["00", ...parsed]));
        }
      } catch (e) {
        return ["00"];
      }
    }
    
    return ["00"];
  });

  // Restore last viewed module from localStorage
  const [currentModule, setCurrentModule] = useState(() => {
    const savedStatus = localStorage.getItem(`status_${accountId}_whmis`);
    const saved = localStorage.getItem("whmis_currentModule");
    // Only restore if it's not stale data
    if (saved && savedStatus !== null) return saved;
    return "00";
  });

  const [selectedAnswers, setSelectedAnswers] = useState(() => {
    // ✅ Always restore saved answers (partial or complete progress)
    const saved = localStorage.getItem("whmis_selectedAnswers");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        console.error("Error parsing saved answers", e);
      }
    }
    
    return Array(quizQuestions.length).fill(null);
  });

  const [quizIndex, setQuizIndex] = useState(() => {
    // ✅ Always restore quiz position (partial or complete progress)
    const saved = localStorage.getItem("whmis_quizIndex");
    return saved ? Number(saved) : 0;
  });

  // quiz
  const [quizStarted, setQuizStarted] = useState(false);
  // const [quizIndex, setQuizIndex] = useState(0);
  // const [selectedAnswers, setSelectedAnswers] = useState(
  //   Array(quizQuestions.length).fill(null)
  // );
  const [isCorrect, setIsCorrect] = useState(null);

  const [quizPassed, setQuizPassed] = useState(() => {
    const savedStatus = localStorage.getItem(`status_${accountId}_${itemId}`);
    return savedStatus === "complete";
  });

  // sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [isPreviouslyComplete, setIsPreviouslyComplete] = useState(false);

  // Called when clicking "COMPLETE" on the very last question
  const finishQuizQuestions = () => {
    setQuizPassed(true); 
    setQuizStarted(false); 
    setQuizIndex(0); 
  };

// Called when clicking "COMPLETE" on the main Quiz Start screen
const finishModule = () => {
  setQuizPassed(true);
  
  // Mark all modules as unlocked when quiz is completed successfully
  const allIds = modules.map(m => m.id);
  setUnlockedModules(allIds);
  
  localStorage.setItem(`status_${accountId}_whmis`, "complete");
  
  localStorage.setItem(
    `form_${accountId}_${itemId}`,
    JSON.stringify({
      unlockedModules: allIds,
      selectedAnswers,
      quizCompleted: true,
    })
  );
  
  localStorage.setItem("whmis_unlockedModules", JSON.stringify(allIds));
  
  window.dispatchEvent(new Event("storage"));
  showSuccess("Form completed successfully!");
  navigate("/register");
};


  // ---------------- AUTO-SCROLL SIDEBAR ----------------
  useEffect(() => {
    const activeItem = document.getElementById(`sidebar-item-${currentModule}`);
    
    if (activeItem) {
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentModule]);

  // Status tracking
  useEffect(() => {
    // Check if have a saved "complete" status already
    const savedStatus = localStorage.getItem(`status_${accountId}_${itemId}`);

    // ✅ FIX: If already complete, don't recalculate or send status updates
    if (savedStatus === "complete") {
      if (!quizPassed) {
        setQuizPassed(true);
        setIsPreviouslyComplete(true);
      }
      return;
    }

    const allModulesUnlocked = modules.every((mod) =>
      unlockedModules.includes(mod.id)
    );
    const quizCompleted = selectedAnswers.every((answer) => answer !== null);

    let status = "incomplete";

    if (allModulesUnlocked && quizCompleted) {
      status = "complete";
    } else if (unlockedModules.length > 1 || quizStarted) {
      status = "in-progress";
    }

    // Only update if status has changed
    if (savedStatus !== status) {
      // Update localStorage
      localStorage.setItem(`status_${accountId}_${itemId}`, status);
      localStorage.setItem(
        `form_${accountId}_${itemId}`,
        JSON.stringify({
          unlockedModules,
          selectedAnswers,
          quizCompleted,
        })
      );

      // Sync status to backend for real-time updates (form data stays in localStorage)
      if (accountId && status !== "incomplete") {
        axios
          .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
            status,
            formData: {},
          })
          .catch((err) => console.error("Failed to sync status:", err));
      }
    }
  }, [unlockedModules, selectedAnswers, quizStarted, accountId, quizPassed]);

  // ---------------- HELPER: UNLOCK MODULE ----------------
  const unlockModule = (id) => {
    setUnlockedModules((prev) => {
      if (!prev.includes(id)) {
        const updated = [...prev, id];
        localStorage.setItem("whmis_unlockedModules", JSON.stringify(updated));
        return updated;
      }
      return prev;
    });
  };

  // ---------------- HELPER: SET MODULE ----------------
  const setModule = (id) => {
    unlockModule(id);
    setCurrentModule(id);
    setQuizStarted(false);
    setIsCorrect(null);
  };

  // ---------------- LOAD PROGRESS ----------------
  // useEffect(() => {
  //   const savedModules = localStorage.getItem("whmis_unlockedModules");
  //   if (savedModules) {
  //     try {
  //       const parsed = JSON.parse(savedModules);
  //       if (Array.isArray(parsed) && parsed.length > 0) {
  //         setUnlockedModules(Array.from(new Set(["00", ...parsed])));
  //       } else {
  //         setUnlockedModules(["00"]);
  //       }
  //     } catch (err) {
  //       setUnlockedModules(["00"]);
  //     }
  //   } else {
  //     setUnlockedModules(["00"]);
  //     localStorage.setItem("whmis_unlockedModules", JSON.stringify(["00"]));
  //   }

  //   const savedAnswers = JSON.parse(
  //     localStorage.getItem("whmis_selectedAnswers")
  //   );
  //   if (savedAnswers && Array.isArray(savedAnswers))
  //     setSelectedAnswers(savedAnswers);

  //   const savedCurrent = localStorage.getItem("whmis_currentModule");
  //   if (savedCurrent) setCurrentModule(savedCurrent);

  //   const savedIndex = localStorage.getItem("whmis_quizIndex");
  //   if (savedIndex) setQuizIndex(Number(savedIndex));
  // }, []);

  useEffect(() => {
    localStorage.setItem(
      "whmis_selectedAnswers",
      JSON.stringify(selectedAnswers)
    );
  }, [selectedAnswers]);

  useEffect(() => {
    localStorage.setItem("whmis_currentModule", currentModule);
  }, [currentModule]);

  useEffect(() => {
    localStorage.setItem("whmis_quizIndex", quizIndex);
  }, [quizIndex]);

  // ---------------- NAVIGATION ----------------
  const goNextModule = () => {
    const idx = modules.findIndex((m) => m.id === currentModule);
    if (idx < modules.length - 1) {
      const nextId = modules[idx + 1].id;
      unlockModule(nextId);
      setModule(nextId);
    }
  };

  const goBackModule = () => {
    const idx = modules.findIndex((m) => m.id === currentModule);
    if (idx > 0) setModule(modules[idx - 1].id);
  };

  const startQuiz = () => {
    setQuizStarted(true);
    const firstAnswer = selectedAnswers[0];
    
    // Check if answer is not null (and not an empty object for matching type)
    const hasAnswer = firstAnswer !== null &&  (typeof firstAnswer !== 'object' || Object.keys(firstAnswer).length > 0);

    if (hasAnswer) {
      // If answer exists, check if it is correct immediately
      checkCorrectness(0, firstAnswer);
    } else {
      // Otherwise, reset to null
      setIsCorrect(null);
    }
  };

  // ---------------- QUIZ HANDLERS ----------------
  const handleAnswerClick = (idx) => {
    setSelectedAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[quizIndex] = idx;
      return newAnswers;
    });
    checkCorrectness(quizIndex, idx);
  };

  const handleMatchingChange = (pairId, choice) => {
    setSelectedAnswers((prev) => {
      const newAnswers = [...prev];
      const current = newAnswers[quizIndex] || {};
      newAnswers[quizIndex] = { ...current, [pairId]: choice };
      return newAnswers;
    });

    const currentAnswers = {
      ...(selectedAnswers[quizIndex] || {}),
      [pairId]: choice,
    };
    checkCorrectness(quizIndex, currentAnswers);
  };

  const checkCorrectness = (qIndex, answer) => {
    const q = activeQuizQuestions[qIndex];
    if (q.type === "matching") {
      if (!answer || Object.keys(answer).length === 0) {
        setIsCorrect(null);
        return;
      }
      const correct = Object.keys(q.answer).every(
        (k) => answer && answer[k] === q.answer[k]
      );
      setIsCorrect(correct);
    } else {
      if (answer == null) {
        setIsCorrect(null);
      } else {
        setIsCorrect(answer === q.answer);
      }
    }
  };

  const nextQuestion = () => {
    if (quizIndex < activeQuizQuestions.length - 1) {
      const nextIdx = quizIndex + 1;
      setQuizIndex(nextIdx);
      const nextAns = selectedAnswers[nextIdx];
      if (
        nextAns == null ||
        (typeof nextAns === "object" && Object.keys(nextAns).length === 0)
      ) {
        setIsCorrect(null);
      } else {
        checkCorrectness(nextIdx, nextAns);
      }
    }
  };

  const prevQuestion = () => {
    if (quizIndex > 0) {
      const prevIdx = quizIndex - 1;
      setQuizIndex(prevIdx);
      const prevAns = selectedAnswers[prevIdx];
      if (
        prevAns == null ||
        (typeof prevAns === "object" && Object.keys(prevAns).length === 0)
      ) {
        setIsCorrect(null);
      } else {
        checkCorrectness(prevIdx, prevAns);
      }
    }
  };

  // ---------------- RENDER ----------------
  return (
    <div className="whmis-container">
      <div className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
        <div className="bar"></div>
        <div className="bar"></div>
        <div className="bar"></div>
      </div>

      <aside className={`whmis-sidebar ${sidebarOpen ? "open" : ""}`}>
        {modules.map((mod) => {
          const isUnlocked = unlockedModules.includes(mod.id);
          return (
            <div
              key={mod.id}
              id={`sidebar-item-${mod.id}`}
              className={`sidebar-item ${
                currentModule === mod.id ? "active" : ""
              } ${isUnlocked ? "completed" : "locked"}`}
              onClick={() => {
                if (!isUnlocked) return;
                setModule(mod.id);
                setSidebarOpen(false);
              }}
            >
              {mod.id} - {mod.title} {isUnlocked && "✔️"}
            </div>
          );
        })}
      </aside>

      <main className="whmis-section">
        <h2>{modules.find((m) => m.id === currentModule)?.title}</h2>

        {currentModule !== "68" && baseContent[currentModule]}

        {currentModule === "68" && (
          <div className="quiz-container">
            {/* ✅ Prerequisite Warning */}
            {showPrerequisiteWarning && !healthSafetyCompleted && (
              <div style={{
                backgroundColor: "#fef3c7",
                border: "2px solid #f59e0b",
                borderRadius: "8px",
                padding: "20px",
                marginBottom: "20px",
                color: "#92400e"
              }}>
                <h3 style={{ margin: "0 0 10px 0", color: "#b45309", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "24px" }}>⚠️</span>
                  <span>Prerequisite Required</span>
                </h3>
                <p style={{ margin: "0 0 10px 0", lineHeight: "1.6" }}>
                  <strong>You must complete the Health & Safety quiz before starting WHMIS.</strong>
                </p>
                <p style={{ margin: "0", lineHeight: "1.6" }}>
                  Please go back to the registration page and complete "Health & Safety" first.
                </p>
                <button 
                  onClick={() => navigate("/register")}
                  style={{
                    marginTop: "15px",
                    padding: "10px 20px",
                    backgroundColor: "#f59e0b",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                >
                  Go to Registration Page
                </button>
              </div>
            )}

            {!quizStarted ? (
              <div>
                <div style={{ marginBottom: "80px" }}> {/* Spacer for sticky footer */}
                  <p>
                    This is the WHMIS Quiz section based on the reading material you
                    just completed.
                  </p>
                  <p>Please complete the Health and Safety Questionnaire.</p>
                </div>

                {/* Sticky Bottom Nav for Quiz Cover Page */}
                <div className="whmis-nav-container">
                  <div className="nav-left-group">
                    <button type="button" className="whmis-back-btn" onClick={goBackModule}>
                      BACK
                    </button>
                    {!quizPassed ? (
                      <button 
                        className="whmis-submit-btn" 
                        onClick={startQuiz}
                        disabled={!healthSafetyCompleted}
                        style={!healthSafetyCompleted ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                      >
                        START QUIZ
                      </button>
                    ) : (
                      <button className="whmis-submit-btn" onClick={finishModule}>
                        COMPLETE
                      </button>
                    )}
                  </div>

                  <button className="whmis-back-btn whmis-nav-right" onClick={() => navigate("/register")}>
                    CLOSE
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {activeQuizQuestions[quizIndex] ? (
                  <>
                    <p>{activeQuizQuestions[quizIndex].question}</p>

                    {activeQuizQuestions[quizIndex].type === "matching" ? (
                  <div className="matching-question">
                    {activeQuizQuestions[quizIndex].pairs.map((pair) => (
                      <div key={pair.id} className="matching-row">
                        <span className="pair-text">{pair.text}</span>
                        <select
                          className="whmis-select"
                          value={selectedAnswers[quizIndex]?.[pair.id] || ""}
                          onChange={(e) =>
                            handleMatchingChange(pair.id, e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          {activeQuizQuestions[quizIndex].options.map((opt) => {
                            const taken = Object.values(
                              selectedAnswers[quizIndex] || {}
                            ).includes(opt);
                            const isThisChoice =
                              selectedAnswers[quizIndex]?.[pair.id] === opt;
                            return (
                              <option
                                key={opt}
                                value={opt}
                                disabled={taken && !isThisChoice}
                              >
                                {opt}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    ))}
                    {isCorrect === false && (
                      <>
                        <p className="feedback wrong">❌ Some answers are wrong</p>

                        {quizIndex === 8 &&
                          activeQuizQuestions[quizIndex].pairs.every(
                            (p) => selectedAnswers[quizIndex]?.[p.id]
                          ) && (
                            <div
                              style={{
                                backgroundColor: "#dc2626", 
                                color: "white",            
                                padding: "15px",
                                borderRadius: "8px",
                                marginTop: "15px",
                                textAlign: "left",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            >
                              <p style={{ fontWeight: "bold", marginBottom: "10px", fontSize: "1.1rem" }}>
                                Hints:
                              </p>
                              <ul style={{ listStyleType: "none", padding: 0, margin: 0, lineHeight: "1.8" }}>
                                <li>Short exposure can cause death: Skull</li>
                                <li>Fire hazard: Flames</li>
                                <li>Health hazard: Man with health issues</li>
                                <li>Gas under pressure: Sealed bottle</li>
                                <li>Skin or eye irritation: Warning</li>
                                <li>Highly self-reactive: Material explodes</li>
                                <li>Corrosive: Burns hands</li>
                                <li>Biohazardous infectious materials: Toxic material</li>
                              </ul>
                            </div>
                          )}

                          {quizIndex === 9 &&
                          activeQuizQuestions[quizIndex].pairs.every(
                            (p) => selectedAnswers[quizIndex]?.[p.id]
                          ) && (
                            <div
                              style={{
                                backgroundColor: "#dc2626",
                                color: "white",
                                padding: "15px",
                                borderRadius: "8px",
                                marginTop: "15px",
                                textAlign: "left",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              }}
                            >
                              <p style={{fontWeight: "bold", marginBottom: "10px", fontSize: "1.1rem",}}>
                                Hints:
                              </p>
                              <ul style={{listStyleType: "none", padding: 0, margin: 0, lineHeight: "1.8",}}
                              >
                                <li>Signal Word: Alerts to the severity of the hazard</li>
                                <li>Hazard Statement: Describes the nature of the hazard</li>
                                <li>Precautionary Statement: Describes the measures to prevent or minimize the effects</li>
                                <li>Product Identity: Name that matches the SDS</li>
                                <li>Supplier Identification: Name, address, and telephone number of supplier</li>
                              </ul>
                            </div>
                          )}
                      </>
                    )}
                    {isCorrect === true && (
                      <p className="feedback correct">✅ All correct!</p>
                    )}
                  </div>
                ) : (
                  <div className="quiz-options-list">
                    {activeQuizQuestions[quizIndex].options.map((opt, idx) => (
                      <label key={idx} className="quiz-option">
                        <input
                          type="checkbox"
                          checked={selectedAnswers[quizIndex] === idx}
                          onChange={() => handleAnswerClick(idx)}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}

                {isCorrect === false &&
                  activeQuizQuestions[quizIndex].type !== "matching" && (
                    <p className="feedback wrong">❌ Wrong answer</p>
                  )}

                
                <div className="quiz-buttons" style={{ marginTop: "2rem" }}>
                  <div className="nav-left-group">
                    <button
                      className="whmis-back-btn"
                      onClick={prevQuestion}
                      disabled={quizIndex === 0}
                    >
                      BACK
                    </button>

                    {quizIndex === activeQuizQuestions.length - 1 ? (
                      <button
                        className="whmis-submit-btn"
                        onClick={finishQuizQuestions} 
                        disabled={!isCorrect}
                      >
                        COMPLETE
                      </button>
                    ) : (
                      <button
                        className="whmis-submit-btn"
                        onClick={nextQuestion}
                        disabled={!isCorrect}
                      >
                        NEXT
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className="whmis-back-btn whmis-close-right"
                    onClick={() => {
                        setQuizIndex(0);
                        setQuizStarted(false);
                    }}
                  >
                    CLOSE
                  </button>
                </div>
                  </>
                ) : (
                  <div className="quiz-error">
                    <p>Quiz data not available. Please refresh the page.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {currentModule !== "68" && (
          <div className="whmis-buttons">
            {/* in-content nav for module pages retained if needed */}
          </div>
        )}
        {/* Sticky bottom navigation */}
        {currentModule !== "68" && (
          <div className="whmis-nav-container">
            <div className="nav-left-group">
              <button type="button" className="whmis-back-btn" onClick={goBackModule}>
                BACK
              </button>
              <button type="button" className="whmis-submit-btn" onClick={goNextModule}>
                NEXT
              </button>
            </div>

            {/* RIGHT CORNER */}
            <button className="whmis-back-btn whmis-nav-right" onClick={() => navigate("/register")}>
              CLOSE
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
